import type { AxiosError, AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { ReleaseOfInformation, CreateROIPayload } from '@/types/releaseOfInformation';
import RepositoryResponse from '@/types/repositoryResponse';
import ajax from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';
import logAjaxError from '@/utils/logAjaxError';

export const releaseOfInformationRepository = {
  BASE_PATH: '/api/dugout/roi/',

  async listForPerson({
    host,
    personId
  }: {
    host: string;
    personId: string;
  }): Promise<RepositoryResponse<ReleaseOfInformation[]>> {
    try {
      const response = await ajax.get({ url: `${host}${this.BASE_PATH}${personId}/` });
      return { data: response.data, isError: false };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(
        error as AxiosError,
        `Failed to list Release of Information for person ${personId}.`
      );
    }
  },

  async create({
    host,
    data
  }: {
    host: string;
    data: CreateROIPayload;
  }): Promise<RepositoryResponse<ReleaseOfInformation>> {
    try {
      const response = await ajax.post<CreateROIPayload, AxiosResponse<ReleaseOfInformation>>({
        url: `${host}${this.BASE_PATH}create/`,
        data
      });
      return { data: response.data, isError: false };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(
        error as AxiosError,
        `Failed to create Release of Information person ${data.person_id}.`
      );
    }
  }
};

const ReleaseOfInformationRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async listForPerson(personId: string): Promise<RepositoryResponse<ReleaseOfInformation[]>> {
      return releaseOfInformationRepository.listForPerson({ host, personId });
    },
    async create(data: CreateROIPayload): Promise<RepositoryResponse<ReleaseOfInformation>> {
      return releaseOfInformationRepository.create({ host, data });
    }
  };
};

export default ReleaseOfInformationRepository;
